import {
  gmailDraftWebHref,
  readNumber,
  readObject,
  readString,
  type ArtifactSummary,
  type TaskRunEvent,
} from "@mlx/contracts";
import { publicRuntimeConfig } from "./public-runtime-config";

// Artifact preview cards.
//
// Any agent tool that produces a viewable resource - a Google Doc, a
// Google Sheet, a Gmail draft, a host document link - surfaces as an
// inline card under the assistant response. MLX core has no artifact
// concept; this is a presentation-layer convenience on top of the
// generic tool-result payload.
//
// A RunArtifact is fully self-describing: the single generic ArtifactCard
// renders it from these fields alone. Supporting a new artifact-producing
// tool means adding one extractor function to ARTIFACT_EXTRACTORS below.

export type RunArtifact = {
  id: string;
  runId?: string;
  sessionId?: string;
  title: string;
  kindLabel: string;
  href?: string;
  contentHref?: string;
  downloadHref?: string;
  pdfHref?: string;
  /** A canvas's assembled document, shown in a sandboxed frame (never the raw source). */
  canvasHref?: string;
  contentKind?: "markdown" | "csv" | "html";
  operation?: "created" | "updated";
  currentVersionId?: string;
  version?: number;
  pendingVersionId?: string;
  pendingVersion?: number;
};

type ArtifactExtractor = (
  toolName: string,
  payload: Record<string, unknown>,
) => RunArtifact | null;

const EDITOR_DOC_CREATE_SUFFIX = "_create_editor_document";
const EDITOR_DOC_REPLACE_SUFFIX = "_replace_editor_document";

function eventPayload(event?: TaskRunEvent) {
  return readObject(event?.payload) ?? {};
}

function googleWorkspaceResult(
  payload: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const result = readObject(payload.result);
  const details = readObject(result?.details);
  const commands = Array.isArray(details?.commands) ? details?.commands : null;
  const first = commands && commands.length > 0 ? commands[0] : undefined;
  return readObject(readObject(first)?.result);
}

const extractEditorDoc: ArtifactExtractor = (toolName, payload) => {
  let operation: "created" | "updated";
  let hostSlug: string;
  if (toolName.endsWith(EDITOR_DOC_CREATE_SUFFIX)) {
    operation = "created";
    hostSlug = toolName.slice(0, -EDITOR_DOC_CREATE_SUFFIX.length);
  } else if (toolName.endsWith(EDITOR_DOC_REPLACE_SUFFIX)) {
    operation = "updated";
    hostSlug = toolName.slice(0, -EDITOR_DOC_REPLACE_SUFFIX.length);
  } else {
    return null;
  }
  const details = readObject(readObject(payload.result)?.details);
  const toolResult = readObject(details?.toolResult);
  if (!toolResult) return null;
  const documentId = readString(toolResult.documentId);
  const title = readString(toolResult.title);
  const version = readNumber(toolResult.version);
  if (!documentId || !title || version === undefined) return null;
  return {
    id: documentId,
    title,
    kindLabel: "Link",
    version,
    operation,
    href: editorDocHref(documentId, hostSlug),
  };
};

function documentOperationKind(
  operation: string,
): "created" | "updated" | null {
  if (!operation) return null;
  if (operation.startsWith("create")) return "created";
  if (
    operation.startsWith("list") ||
    operation.startsWith("get") ||
    operation.startsWith("read") ||
    operation.startsWith("search")
  ) {
    return null;
  }
  return "updated";
}

const extractGoogleDoc: ArtifactExtractor = (toolName, payload) => {
  if (!toolName.includes("docs")) return null;
  const operation = documentOperationKind(
    readString(readObject(payload.args)?.operation) ?? "",
  );
  if (!operation) return null;
  const result = googleWorkspaceResult(payload);
  if (!result || typeof result.documentId !== "string") return null;
  return {
    id: result.documentId,
    title: readString(result.title) ?? "Google Doc",
    kindLabel: "Google Doc",
    operation,
    href: `https://docs.google.com/document/d/${encodeURIComponent(result.documentId)}/edit`,
  };
};

const extractGoogleSheet: ArtifactExtractor = (toolName, payload) => {
  if (!toolName.includes("sheets")) return null;
  const operation = documentOperationKind(
    readString(readObject(payload.args)?.operation) ?? "",
  );
  if (!operation) return null;
  const result = googleWorkspaceResult(payload);
  if (!result || typeof result.spreadsheetId !== "string") return null;
  const properties = readObject(result.properties);
  return {
    id: result.spreadsheetId,
    title: readString(properties?.title) ?? "Google Sheet",
    kindLabel: "Google Sheet",
    operation,
    href:
      readString(result.spreadsheetUrl) ??
      `https://docs.google.com/spreadsheets/d/${encodeURIComponent(result.spreadsheetId)}/edit`,
  };
};

const extractGmailDraft: ArtifactExtractor = (toolName, payload) => {
  if (!toolName.includes("gmail")) return null;
  const result = googleWorkspaceResult(payload);
  if (!result || typeof result.id !== "string") return null;
  const message = readObject(result.message);
  const labelIds = Array.isArray(message?.labelIds) ? message?.labelIds : [];
  if (!labelIds.some((label) => label === "DRAFT")) return null;
  const args = readObject(payload.args);
  return {
    id: result.id,
    title:
      readString(result.subject) ?? readString(args?.subject) ?? "Email draft",
    kindLabel: "Email draft",
    operation: "created",
    href: gmailDraftWebHref(result.id),
  };
};

const extractSourceAction: ArtifactExtractor = (toolName, payload) => {
  if (toolName !== "twin_workspace_record_source_action") return null;
  const details = readObject(readObject(payload.result)?.details);
  const receipt = readObject(details?.receipt);
  const status = readString(receipt?.status);
  const href = readString(receipt?.source_url);
  if (!href || (status !== "succeeded" && status !== "reconciled")) return null;
  const id =
    readString(receipt?.external_id) ??
    readString(receipt?.source_receipt_id) ??
    readString(receipt?.id);
  const sourceSystem = readString(receipt?.source_system);
  if (!id || !sourceSystem) return null;
  const actionType = readString(receipt?.action_type);
  return {
    id,
    title:
      readString(receipt?.subject) ??
      readString(receipt?.action_label) ??
      `${sourceSystem} ${readString(receipt?.source_entity) ?? "record"}`,
    kindLabel: "External record",
    operation: actionType?.includes("created") ? "created" : "updated",
    href,
  };
};

const ARTIFACT_EXTRACTORS: ArtifactExtractor[] = [
  extractMlxCanvas,
  extractMlxCsvArtifactUpdate,
  extractMlxArtifactRevision,
  extractEditorDoc,
  extractGoogleDoc,
  extractGoogleSheet,
  extractGmailDraft,
  extractSourceAction,
];

const CANVAS_TOOLS = new Set(["twin_create_canvas", "twin_revise_canvas"]);

/** A canvas the Twin designed or revised: every version is accepted, so the result is the current one. */
function extractMlxCanvas(
  toolName: string,
  payload: Record<string, unknown>,
): RunArtifact | null {
  if (!CANVAS_TOOLS.has(toolName)) return null;
  const details = readObject(readObject(payload.result)?.details);
  const toolResult = readObject(details?.toolResult);
  const artifactId = readString(toolResult?.artifactId);
  const versionId = readString(toolResult?.versionId);
  const title = readString(toolResult?.title);
  const version = readNumber(toolResult?.version);
  if (!artifactId || !versionId || !title || version === undefined) return null;
  return {
    ...canvasLinks(artifactId),
    id: artifactId,
    title,
    kindLabel: "Canvas",
    version,
    currentVersionId: versionId,
    operation: toolName === "twin_create_canvas" ? "created" : "updated",
    contentKind: "html",
  };
}

/** Where a canvas is read: its assembled document, and the self-contained file Download saves. */
export function canvasLinks(artifactId: string): Pick<RunArtifact, "contentHref" | "canvasHref" | "downloadHref"> {
  const base = `/api/artifacts/${encodeURIComponent(artifactId)}`;
  return {
    contentHref: `${base}/content`,
    canvasHref: `${base}/canvas`,
    downloadHref: `${base}/canvas?download=1`,
  };
}

/** A canvas full screen: Present. */
export function canvasPresentHref(artifactId: string): string {
  return `/canvas/${encodeURIComponent(artifactId)}`;
}

function extractMlxCsvArtifactUpdate(
  toolName: string,
  payload: Record<string, unknown>,
): RunArtifact | null {
  if (toolName !== "twin_update_csv_artifact") return null;
  const artifact = mlxStoredArtifactFromToolResult(payload, "csv");
  if (!artifact) return null;
  const { versionId, ...runArtifact } = artifact;
  return {
    ...runArtifact,
    kindLabel: "Spreadsheet",
    contentKind: "csv",
    currentVersionId: versionId,
  };
}

function mlxStoredArtifactFromToolResult(
  payload: Record<string, unknown>,
  kind: "csv" | "markdown",
): (RunArtifact & { versionId: string }) | null {
  const details = readObject(readObject(payload.result)?.details);
  const toolResult = readObject(details?.toolResult);
  if (!toolResult) return null;
  const artifactId = readString(toolResult.artifactId);
  const versionId = readString(toolResult.versionId);
  const title = readString(toolResult.title);
  const version = readNumber(toolResult.version);
  if (!artifactId || !versionId || !title || version === undefined) return null;
  const contentHref = `/api/artifacts/${encodeURIComponent(artifactId)}/content`;
  const pdfHref =
    kind === "markdown"
      ? `/api/artifacts/${encodeURIComponent(artifactId)}/pdf`
      : undefined;
  return {
    id: artifactId,
    title,
    kindLabel: kind === "csv" ? "Spreadsheet" : "Document",
    version,
    versionId,
    operation: "updated",
    contentHref,
    downloadHref: `${contentHref}?download=1`,
    pdfHref,
    contentKind: kind,
  };
}

function extractMlxArtifactRevision(
  toolName: string,
  payload: Record<string, unknown>,
): RunArtifact | null {
  if (toolName !== "twin_propose_markdown_artifact_revision") return null;
  const artifact = mlxStoredArtifactFromToolResult(payload, "markdown");
  if (!artifact) return null;
  const { versionId, ...runArtifact } = artifact;
  return {
    ...runArtifact,
    pdfHref: runArtifact.pdfHref
      ? `${runArtifact.pdfHref}?versionId=${encodeURIComponent(versionId)}`
      : undefined,
    pendingVersionId: versionId,
    pendingVersion: runArtifact.version,
  };
}

function readRunArtifact(event: TaskRunEvent): RunArtifact | null {
  if (event.kind !== "tool.completed") return null;
  const payload = eventPayload(event);
  const toolName = readString(payload.toolName);
  if (!toolName) return null;
  for (const extract of ARTIFACT_EXTRACTORS) {
    const artifact = extract(toolName, payload);
    if (artifact) return artifact;
  }
  return null;
}

export function getRunArtifacts(events?: TaskRunEvent[]): RunArtifact[] {
  if (!events?.length) return [];
  const byId = new Map<string, RunArtifact>();
  for (const event of events) {
    const artifact = readRunArtifact(event);
    if (!artifact) continue;
    const existing = byId.get(artifact.id);
    if (existing?.operation === "created" && artifact.operation === "updated") {
      continue;
    }
    if (!existing || (artifact.version ?? 0) >= (existing.version ?? 0)) {
      byId.set(artifact.id, artifact);
    }
  }
  return Array.from(byId.values());
}

export function artifactSummaryToRunArtifact(
  artifact: ArtifactSummary,
): RunArtifact | null {
  if (artifact.kind === "external-resource" && artifact.href) {
    return {
      id: artifact.externalId,
      runId: artifact.runId,
      sessionId: artifact.sessionId,
      title: artifact.title,
      kindLabel: externalArtifactKindLabel(artifact.provider),
      href: artifact.href,
      operation: artifact.operation ?? "created",
    };
  }
  if (artifact.provider === "mlx" && artifact.kind === "html") {
    return {
      ...canvasLinks(artifact.id),
      id: artifact.id,
      runId: artifact.runId,
      sessionId: artifact.sessionId,
      title: artifact.title,
      kindLabel: "Canvas",
      version: artifact.version,
      operation: artifact.operation ?? "created",
      currentVersionId: artifact.currentVersionId,
      contentKind: "html",
    };
  }
  if (
    artifact.provider !== "mlx" ||
    (artifact.kind !== "markdown" && artifact.kind !== "csv")
  ) {
    return null;
  }
  const contentHref = `/api/artifacts/${encodeURIComponent(artifact.id)}/content`;
  const isCsv = artifact.kind === "csv";
  const pdfHref = isCsv
    ? undefined
    : artifact.pendingVersionId
      ? `/api/artifacts/${encodeURIComponent(artifact.id)}/pdf?versionId=${encodeURIComponent(artifact.pendingVersionId)}`
      : `/api/artifacts/${encodeURIComponent(artifact.id)}/pdf`;
  return {
    id: artifact.id,
    runId: artifact.runId,
    sessionId: artifact.sessionId,
    title: artifact.title,
    kindLabel: isCsv ? "Spreadsheet" : "Document",
    version: artifact.version,
    operation: artifact.operation ?? "created",
    currentVersionId: artifact.currentVersionId,
    contentHref,
    downloadHref: `${contentHref}?download=1`,
    pdfHref,
    contentKind: artifact.kind,
    pendingVersionId: artifact.pendingVersionId,
    pendingVersion: artifact.pendingVersion,
  };
}

function externalArtifactKindLabel(
  provider: ArtifactSummary["provider"],
): string {
  switch (provider) {
    case "google-doc":
      return "Google Doc";
    case "google-sheet":
      return "Google Sheet";
    case "gmail-draft":
      return "Email draft";
    case "source-action":
      return "External record";
    case "tallie-editor-document":
    case "mlx":
      return "Link";
  }
}

export function editorDocHref(documentId: string, hostSlug?: string): string {
  const slugUpper = hostSlug?.replace(/[^a-z0-9]+/giu, "_").toUpperCase();
  const config = publicRuntimeConfig();
  const perHost = slugUpper ? config.editorHosts[slugUpper] : undefined;
  const base = (perHost?.baseUrl ?? config.editorHostBaseUrl ?? "").replace(
    /\/$/u,
    "",
  );
  const path =
    perHost?.docPath ?? config.editorHostDocPath ?? "/documents/{id}";
  return `${base}${path.replace("{id}", encodeURIComponent(documentId))}`;
}
